import React, { useEffect, useState, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams } from 'react-router-dom';
import { FaCheck, FaTimes } from 'react-icons/fa';
import translations from '../../translations/de';
import Paths from '../../constants/paths';
import Breadcrumb from '../../components/Breadcrumb';
import TabSwitcher from '../../components/TabSwitcher';
import Table from '../../components/Table';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Modal from '../../components/Modal';
import useModal from '../../customHooks/useModal';
import ErrorMessages from '../../components/ErrorMessages.js';
import parkingAPI from '../../api/parking';
import formatPin from '../../helpers/formatPin';
import { formatDate } from '../../helpers/formatDate';
import requsetErrorMessage from '../../helpers/requsetErrorMessage';
import AssignOwnerModal from '../ParkingFields/AssignOwnerModal';
import ParkingPassPrint from './ParkingPassPrint';

const emptyRecipient = {
    recipient_first_name: '',
    recipient_last_name: '',
    recipient_email: '',
    recipient_phone: '',
};

// One labeled read-only row in the "Allgemein" info panel.
const InfoRow = ({ label, value }) => (
    <div style={{ display: 'flex', padding: '10px 0', borderBottom: '1px solid #eee' }}>
        <div
            style={{
                width: 220,
                minWidth: 220,
                fontWeight: 'bold',
                textTransform: 'uppercase',
                fontSize: 13,
                color: '#666',
            }}
        >
            {label}
        </div>
        <div style={{ color: '#222' }}>{value}</div>
    </div>
);

// Red section header, echoing the Abo detail look.
const SectionTitle = ({ children }) => (
    <div
        style={{
            background: '#e73c37',
            color: '#fff',
            fontWeight: 'bold',
            padding: '10px 15px',
            borderRadius: '3px 3px 0 0',
        }}
    >
        {children}
    </div>
);

// Admin eticket view for one parking pass: header + per-game codes with
// used/assignment status, and per-game assignment to a recipient (admin share).
const ParkingPass = ({ store }) => {
    const { pass_id } = useParams();
    const modalRef = useRef();
    const [modal, setModal] = useModal(false, modalRef);
    const [pass, setPass] = useState(null);
    const [error, setError] = useState(null);
    const [activeAccessId, setActiveAccessId] = useState(null);
    const [recipient, setRecipient] = useState(emptyRecipient);
    // Whole-pass owner assign/change/remove modal.
    const [ownerModal, setOwnerModal] = useState(false);
    // access_code_id whose eticket link was just copied (for the "Kopiert!" hint).
    const [copiedId, setCopiedId] = useState(null);

    const copyShareLink = async (accessId) => {
        setError(null);
        try {
            const res = await parkingAPI.shareLink(accessId);
            await navigator.clipboard.writeText(res.data.url);
            setCopiedId(accessId);
            setTimeout(() => setCopiedId(null), 2000);
        } catch (e) {
            setError(requsetErrorMessage(e && e.message ? e.message : e));
        }
    };

    const load = async () => {
        try {
            const res = await parkingAPI.getPass(pass_id);
            setPass(res.data);
        } catch (e) {
            setError(requsetErrorMessage(e && e.message ? e.message : e));
        }
    };

    useEffect(() => {
        !store.dataLoaded && store.setDataLoaded();
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pass_id]);

    if (!pass) {
        return (
            <div style={{ margin: '0 2.5%' }}>
                <ErrorMessages messages={error || ''} />
            </div>
        );
    }

    const holderName = [pass.holder.first_name, pass.holder.last_name].filter(Boolean).join(' ') || '—';

    const ownerName = pass.owner
        ? [pass.owner.first_name, pass.owner.last_name].filter(Boolean).join(' ') ||
          pass.owner.email ||
          `#${pass.owner.id}`
        : '';

    // Chrome names the "Save as PDF" file after document.title, which is the app
    // shell's title and says nothing about the card. Swapped for the duration of
    // the dialog and put back on afterprint, since the dialog reads the title
    // when it opens, not when print() returns.
    const printSheets = () => {
        const previous = document.title;
        const name = [
            'Parkkarte',
            formatPin(pass.assign_token),
            pass.field?.name,
            ownerName || (holderName === '—' ? '' : holderName),
        ]
            .filter(Boolean)
            .join(' - ')
            // Slashes would be read as path separators by the save dialog.
            .replace(/[\\/]/g, '-');

        const restore = () => {
            document.title = previous;
            window.removeEventListener('afterprint', restore);
        };
        window.addEventListener('afterprint', restore);

        document.title = name;
        window.print();
    };

    const openAssign = (accessId) => {
        setActiveAccessId(accessId);
        setRecipient(emptyRecipient);
        setError(null);
        setModal(true);
    };

    const submitAssign = async () => {
        try {
            await parkingAPI.assignAccess(activeAccessId, recipient);
            setModal(false);
            await load();
        } catch (e) {
            setError(requsetErrorMessage(e && e.message ? e.message : e));
        }
    };

    const revoke = async (accessId) => {
        try {
            await parkingAPI.revokeAccess(accessId);
            await load();
        } catch (e) {
            setError(requsetErrorMessage(e && e.message ? e.message : e));
        }
    };

    const columns = [
        { Header: 'Spiel', accessor: 'spiel', Cell: ({ value }) => value },
        {
            Header: 'Zugewiesen an',
            accessor: 'assignedTo',
            Cell: ({ value }) => <div>{value}</div>,
        },
        {
            Header: 'Genutzt',
            accessor: 'used',
            Cell: ({ value }) => (
                <div>{value ? <FaCheck className="confirm" /> : <FaTimes className="error" />}</div>
            ),
        },
        {
            Header: '',
            accessor: 'action',
            Cell: ({ row }) => (
                <div className="button-cell">
                    {row.original.assigned ? (
                        <Button title="Entziehen" onClick={() => revoke(row.original.accessId)}>
                            Entziehen
                        </Button>
                    ) : (
                        <Button title="Zuweisen" onClick={() => openAssign(row.original.accessId)}>
                            Zuweisen
                        </Button>
                    )}
                    <Button
                        title="Link kopieren"
                        onClick={() => copyShareLink(row.original.accessId)}
                    >
                        {copiedId === row.original.accessId ? 'Kopiert!' : 'Link'}
                    </Button>
                </div>
            ),
        },
    ];

    const data = pass.games.map((g) => ({
        accessId: g.access_code_id,
        assigned: !!g.recipient,
        spiel: (
            <div style={{ whiteSpace: 'nowrap' }}>
                {g.away_team?.logo_url && (
                    <img
                        src={g.away_team.logo_url}
                        alt=""
                        style={{ height: 22, width: 22, marginRight: 8, objectFit: 'contain', verticalAlign: 'middle' }}
                    />
                )}
                <span style={{ verticalAlign: 'middle' }}>{g.date ? formatDate(g.date) : ''}</span>
            </div>
        ),
        assignedTo: g.recipient
            ? [g.recipient.first_name, g.recipient.last_name].filter(Boolean).join(' ') ||
              g.recipient.email ||
              '—'
            : '—',
        used: !!g.used_at,
    }));

    const typeLabel = pass.type === 'full_season' ? translations.full_season : 'Einzelspiel';
    const hasHolder = !!(pass.holder.first_name || pass.holder.last_name || pass.holder.email || pass.holder.phone);

    return (
        <div style={{ width: '100%' }}>
            <div style={{ margin: '0 2.5% 15px' }}>
                <Breadcrumb
                    links={[
                        { path: Paths.PARKING_FIELDS, name: translations.parking_fields },
                        {
                            path: Paths.PARKING_FIELD.replace(':field_id', pass.field.id),
                            name: pass.field.name,
                        },
                    ]}
                    name={ownerName || holderName}
                />
            </div>

            <TabSwitcher initialTabName={translations.general}>
                <TabSwitcher.Tabs>
                    <TabSwitcher.TabPill tabName={translations.general} />
                    <TabSwitcher.TabPill tabName={translations.passes_column} />
                </TabSwitcher.Tabs>

                <TabSwitcher.TabPanel activeWhenTabName={translations.general}>
                    <div style={{ margin: '0 2.5%' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginBottom: 15 }}>
                            {/* Paper fallback for a holder without a phone: one
                                sheet per game, printed from the data already on
                                this page. */}
                            <Button title="Alle Codes drucken" onClick={printSheets}>
                                Alle Codes drucken
                            </Button>

                            <Button
                                title={pass.owner ? 'Besitzer ändern' : 'Besitzer zuteilen'}
                                onClick={() => setOwnerModal(true)}
                            >
                                {pass.owner ? 'Besitzer ändern' : 'Besitzer zuteilen'}
                            </Button>
                        </div>

                        <div style={{ border: '1px solid #eee', borderRadius: 3, marginBottom: 25 }}>
                            <SectionTitle>{translations.general}</SectionTitle>
                            <div style={{ padding: '5px 20px 15px' }}>
                                <InfoRow label={translations.parking_field} value={pass.field.name} />
                                <InfoRow label={translations.type} value={typeLabel} />
                                <InfoRow label={translations.parking_code} value={formatPin(pass.assign_token)} />
                                <InfoRow
                                    label="Status"
                                    value={pass.activated ? 'Aktiviert' : 'Nicht aktiviert'}
                                />
                                <InfoRow label="Zugewiesen" value={`${pass.assigned}/${pass.total}`} />
                                <InfoRow label="Genutzt" value={`${pass.used}/${pass.total}`} />
                            </div>
                        </div>

                        <div style={{ border: '1px solid #eee', borderRadius: 3 }}>
                            <SectionTitle>Besitzer</SectionTitle>
                            <div style={{ padding: '5px 20px 15px' }}>
                                <InfoRow
                                    label="Besitzer"
                                    value={
                                        pass.owner ? (
                                            <a href={`/fans/${pass.owner.id}`}>{ownerName}</a>
                                        ) : (
                                            '—'
                                        )
                                    }
                                />
                                {pass.owner?.email && (
                                    <InfoRow label="Email" value={pass.owner.email} />
                                )}
                                {hasHolder && (
                                    <InfoRow
                                        label="Inhaber (Kontakt)"
                                        value={
                                            [pass.holder.first_name, pass.holder.last_name]
                                                .filter(Boolean)
                                                .join(' ') ||
                                            pass.holder.email ||
                                            pass.holder.phone
                                        }
                                    />
                                )}
                            </div>
                        </div>

                        <div style={{ marginTop: 15 }}>
                            <ErrorMessages messages={error || ''} />
                        </div>
                    </div>
                </TabSwitcher.TabPanel>

                <TabSwitcher.TabPanel activeWhenTabName={translations.passes_column}>
                    <div style={{ margin: '0 2.5%' }}>
                        <ErrorMessages messages={error || ''} />
                    </div>
                    <Table noIdColumn tableName="" columns={columns} data={data} />
                </TabSwitcher.TabPanel>
            </TabSwitcher>

            <ParkingPassPrint pass={pass} holderName={ownerName || (holderName === '—' ? '' : holderName)} />

            {modal && (
                <Modal title="Parkkarte zuweisen" onClose={() => setModal(false)} ref={modalRef} maxWidth="520px">
                    <Input
                        label="Vorname"
                        value={recipient.recipient_first_name}
                        onChange={(e) =>
                            setRecipient({ ...recipient, recipient_first_name: e.target.value })
                        }
                    />
                    <Input
                        label="Nachname"
                        value={recipient.recipient_last_name}
                        onChange={(e) =>
                            setRecipient({ ...recipient, recipient_last_name: e.target.value })
                        }
                    />
                    <Input
                        label="Email"
                        value={recipient.recipient_email}
                        onChange={(e) =>
                            setRecipient({ ...recipient, recipient_email: e.target.value })
                        }
                    />
                    <Input
                        label="Telefon"
                        value={recipient.recipient_phone}
                        onChange={(e) =>
                            setRecipient({ ...recipient, recipient_phone: e.target.value })
                        }
                    />
                    <div className="Modal-Controls" style={{ marginTop: '20px' }}>
                        <Button label="Zuweisen" onClick={submitAssign} />
                        <Button
                            label={translations.cancel}
                            theme="gray"
                            onClick={() => setModal(false)}
                            style={{ marginLeft: '15px' }}
                        />
                    </div>
                </Modal>
            )}

            {ownerModal && (
                <AssignOwnerModal
                    passId={pass.id}
                    currentOwner={ownerName}
                    onClose={() => setOwnerModal(false)}
                    onAssigned={() => {
                        setOwnerModal(false);
                        load();
                    }}
                />
            )}
        </div>
    );
};

export default observer(ParkingPass);
